import axios from 'axios';
import amplitude from 'amplitude-js';
import { notificationService } from 'views/ui/NotificationService';
import { LoginState } from 'redux/selectors/ui';
import { CountryCode, LanguageCode } from 'config/pais';
import { fetchGetEstudianteDetail } from 'api/getEstudiante';
import { TipoUsuarioEnum } from 'enums/tipoUsuario';
import i18n from 'i18n';
import { fetchGetMonitorDetail } from 'api/monitor';
import { IUserItem } from 'views/auth/LoginRedesign/AccountSelectionModal';
import { ActividadTipoEnum } from 'enums/rutaSesionTipo';
import firebase from '../../config/firebase';
import { authenticated, logout } from './authSlice';
import { updateProfesor } from './profesorSlice';
import { fetchGetProfesorDetail } from '../../api/profesor';
import { API_KEY } from '../../constants';
import { isLoading } from './appSlice';
import { loginStatus } from './uiSlice';
import { getPermissionsByRole } from '../../rbac/roles';
import { setLangCountryUser } from './langCountryUserSlice';
import { resetCurrentSesionState } from './currentSesionStateSlice';
import { resetSelectedSesionState } from './selectedSesionStateSlice';
import { resetEvaluacionState } from './evaluacionesStateSlice';
import { setCurrentTipoActividadState } from './currentTipoActividadStateSlice';
import { setNotificaciones } from './notificacionesSlice';
import { resetCurrentDesafioCursoState } from './currentDesafioCursoStateSlice';
import { resetSelectedEstudianteState } from './selectedEstudianteStateSlice';
import { resetCursoProfesorDesafio } from './cursoProfesorDesafioStateSlice';

export interface IUserClaimsAuth {
	a: 0 | 1; // authorized flag con valores específicos
	l: {
		p: string[]; // profesores
		c: string[]; // coordinadores
		m: string[]; // monitores
		u: string; // user Key
	};
	swarmobver: number; // versión
	authorized: boolean; // bandera de autorización
}

export const startLoad = () => (dispatch: any) => {
	dispatch(isLoading(true));
};

export const finishLoad = () => (dispatch: any) => {
	dispatch(isLoading(false));
};

export const updateLoginStatus = (status: LoginState) => (dispatch: any) => {
	dispatch(loginStatus(status));
};

export const logoutAction = (text?: string) => {
	console.log('Ejecución logoutAction');
	return async (dispatch: any) => {
		try {
			// Clear verified emails cookie by sending request to backend
			await axios
				.post(
					`${API_KEY}/auth/clearVerifiedEmails`,
					{},
					{
						withCredentials: true
					}
				)
				.catch((err) => {
					// Just log if this fails, don't block logout
					console.error('Failed to clear verified emails cookie:', err);
				});

			// Get all initialized Firebase apps and delete secondary instances
			const { apps } = firebase;
			const deletePromises = apps.filter((app) => app.name !== '[DEFAULT]').map((app) => app.delete());

			// Wait for all secondary apps to be deleted
			if (deletePromises.length > 0) {
				await Promise.all(deletePromises);
				console.log(`Deleted ${deletePromises.length} secondary Firebase apps`);
			}

			// Now sign out from the main app
			await firebase
				.auth()
				.signOut()
				.then(() => {
					console.log('logoutAction dispatch');
					dispatch(resetCurrentSesionState());
					dispatch(resetSelectedSesionState());
					dispatch(resetEvaluacionState());
					dispatch(setCurrentTipoActividadState(ActividadTipoEnum.grupal));

					dispatch(setNotificaciones([]));
					dispatch(resetCurrentDesafioCursoState());
					dispatch(resetSelectedEstudianteState());
					dispatch(resetCursoProfesorDesafio());
					localStorage.removeItem('pageReloaded');
					dispatch(logout());
					if (text === 'reload') {
						window.location.replace('/');
					}
				});
		} catch (error) {
			console.error('Error during logout:', error);
			// Even if there's an error, try to dispatch logout
			dispatch(logout());
		}
	};
};

export const authenticate = (account?: IUserItem) => (dispatch: any) => {
	firebase.auth().onAuthStateChanged((user: any) => {
		if (!user) {
			dispatch(updateLoginStatus('logout'));
			dispatch(logoutAction());
			finishLoad();
			return;
		}
		const isStandalone =
			window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

		user.getIdTokenResult(true).then(async (idTokenResult: any) => {
			const mergedUser = { ...user, ...idTokenResult.claims };

			const currentAppVersion = 2; // Your new version with combined tables

			let listaColegios;
			let avatarPath;
			let cambioPass;
			if (mergedUser.tipoUsuario === TipoUsuarioEnum.estudiante) {
				const userContent = {
					hasuraKey: mergedUser.hasuraKey,
					tipoUsuario: mergedUser.tipoUsuario,
					uid: mergedUser.uid,
					user_id: mergedUser.user_id,
					nombre: mergedUser.nombre,
					displayName: mergedUser.displayName,
					colegio: mergedUser.colegio,
					rol: mergedUser.rol
					// ...idTokenResult.claims
				};
				const permissions = getPermissionsByRole(userContent.tipoUsuario);
				const estudiante = await fetchGetEstudianteDetail(userContent.hasuraKey);

				if (!estudiante?.key) {
					dispatch(logout());
					dispatch(updateLoginStatus('logout'));
					finishLoad();
					notificationService({ info: true }, 'Usuario no habilitado');

					return;
				}

				const dataIdioma = localStorage.getItem('lng')?.split('-');
				const langData = estudiante?.colegio?.cursos?.[0]?.idioma || 'es';
				const paisData = estudiante?.colegio?.pais || 'CL';

				if (!dataIdioma || dataIdioma?.[2] === 'login') {
					localStorage.setItem('lng', `${langData}-${paisData}`);
					dispatch(
						setLangCountryUser({
							lang: langData as LanguageCode,
							country: paisData as CountryCode
						})
					);
					i18n.changeLanguage(`${langData}-${paisData}`);
				} else {
					dispatch(
						setLangCountryUser({
							lang: dataIdioma[0] as LanguageCode,
							country: dataIdioma[1] as CountryCode
						})
					);
					i18n.changeLanguage(`${dataIdioma[0]}-${dataIdioma[1]}`);
				}
				avatarPath = estudiante.avatarPath;
				cambioPass = estudiante.cambioPass;

				// dispatch(authenticated({ ...userContent, permissions }));
				dispatch(authenticated({ ...userContent, permissions, listaColegios, avatarPath, cambioPass }));
				dispatch(updateLoginStatus('login'));
				finishLoad();
				return;
			}

			const userVersion = mergedUser.swarmobver || 1;

			// if (userVersion !== currentAppVersion) {
			// 	console.log('Version mismatch, logging out...');
			// 	// Schema changed, force logout
			// 	dispatch(updateLoginStatus('logout'));
			// 	await dispatch(logoutAction());
			// 	finishLoad();
			// 	notificationService(
			// 		{ info: true },
			// 		'Se requiere iniciar sesión nuevamente por actualización del sistema'
			// 	);
			// 	// setTimeout(() => {
			// 	// 	window.location.href = '/login';
			// 	// }, 2000);
			// 	return;
			// }

			const hasuraKey = localStorage.getItem('key') ?? '';
			const tipoUsuario = localStorage.getItem('tp')?.toLowerCase();

			if (mergedUser.admin) {
				const userContent = {
					hasuraKey: hasuraKey || account?.hasuraKey ? hasuraKey || account?.hasuraKey : null,
					tipoUsuario: TipoUsuarioEnum.profesor,
					uid: mergedUser.uid,
					user_id: mergedUser.user_id,
					nombre: account?.nombre,
					displayName: mergedUser.displayName,
					avatarPath: user?.avatarPath || account?.avatarPath
				};
				dispatch(authenticated({ ...userContent, tipoUsuario: 'admin' }));
				dispatch(updateLoginStatus('login'));
				finishLoad();
				return;
			}

			if (
				tipoUsuario === TipoUsuarioEnum.profesor ||
				account?.tipoUsuario === TipoUsuarioEnum.profesor ||
				tipoUsuario === TipoUsuarioEnum.coordinador ||
				account?.tipoUsuario === TipoUsuarioEnum.coordinador
			) {
				console.log('[UserAuth] fetch profesor data');
				const userCuenta = await fetchGetProfesorDetail(hasuraKey ?? account?.hasuraKey, isStandalone);

				const userData = userCuenta?.user;
				const profesor = userCuenta;

				const userContent = {
					hasuraKey: hasuraKey || account?.hasuraKey,
					tipoUsuario: (tipoUsuario ?? account?.tipoUsuario) as TipoUsuarioEnum,
					uid: mergedUser.uid,
					user_id: mergedUser.user_id,
					nombre: account?.nombre || userData?.nombre || user?.nombre,
					apellido: account?.apellido || userData?.apellido || user?.apellido,
					displayName: mergedUser.displayName,
					colegio: account?.colegioKey || profesor?.colegio?.key,
					rol: userCuenta?.rol || account?.rol || profesor?.rol || mergedUser?.rol,
					userKey: account?.userKey || userData?.key || mergedUser?.l?.u,
					pais: profesor?.pais,
					avatarPath: userData?.avatarPath || user?.avatarPath
				};

				console.log('[UserAuth] userContent: ', tipoUsuario);
				const permissions = getPermissionsByRole(userContent.tipoUsuario);

				if (!profesor?.key) {
					dispatch(logout());
					dispatch(updateLoginStatus('logout'));
					finishLoad();
					notificationService({ info: true }, 'Usuario no habilitado');
					return;
				}

				const dataIdioma = localStorage.getItem('lng')?.split('-');
				const lang = (profesor?.colegio?.idioma as LanguageCode) || 'es';
				const country = (profesor?.colegio?.pais as CountryCode) || 'CL';
				const isLogin = dataIdioma?.[2] === 'login';

				if (!dataIdioma || isLogin) {
					localStorage.setItem('lng', `${lang}-${country}`);
					dispatch(
						setLangCountryUser({
							lang,
							country
						})
					);
					i18n.changeLanguage(`${lang}-${country}`);
				} else {
					dispatch(
						setLangCountryUser({
							lang: dataIdioma[0] as LanguageCode,
							country: dataIdioma[1] as CountryCode
						})
					);
					i18n.changeLanguage(`${dataIdioma[0]}-${dataIdioma[1]}`);
				}

				console.log(dataIdioma, lang, country);

				localStorage.setItem('profesorKey', profesor.key);

				console.log(
					`[UserAuth] profesor(${profesor.key}) ${profesor?.user?.nombre} loaded for country ${profesor.pais}`
				);
				dispatch(updateProfesor(profesor));
				avatarPath = userData?.avatarPath || user?.avatarPath;
				dispatch(authenticated({ ...userContent, permissions, listaColegios, avatarPath, cambioPass }));
				dispatch(updateLoginStatus('login'));
				finishLoad();
				return;
			}

			if (tipoUsuario === TipoUsuarioEnum.monitor || account?.tipoUsuario === TipoUsuarioEnum.monitor) {
				console.log('[UserAuth] fetch monitor data');
				const userCuenta = await fetchGetMonitorDetail(hasuraKey ?? account?.hasuraKey, isStandalone);

				const userData = userCuenta?.user;
				const monitor = userCuenta;

				const userContent = {
					hasuraKey: hasuraKey || account?.hasuraKey,
					tipoUsuario: TipoUsuarioEnum.monitor,
					uid: mergedUser.uid,
					user_id: mergedUser.user_id,
					nombre: account?.nombre || userData?.nombre || user?.nombre,
					apellido: account?.apellido || userData?.apellido || user?.apellido,
					displayName: mergedUser.displayName,
					institucion: account?.institucionKey || monitor?.institucion_key,
					rol: account?.rol || monitor.rol || mergedUser.rol,
					userKey: account?.userKey || userData?.key || mergedUser?.l?.u,
					pais: monitor?.pais
				};
				console.log('check', account, userData, mergedUser);
				console.log('[UserAuth] userContent: ', userContent, userVersion);
				const permissions = getPermissionsByRole(userContent.tipoUsuario);

				if (!monitor?.key) {
					dispatch(logout());
					dispatch(updateLoginStatus('logout'));
					finishLoad();
					notificationService({ info: true }, 'Usuario no habilitado');
					return;
				}

				const dataIdioma = localStorage.getItem('lng')?.split('-');
				const lang = (monitor?.monitor_colegios?.[0]?.colegio?.idioma as LanguageCode) || 'es';
				const country = (monitor?.monitor_colegios?.[0]?.colegio?.pais as CountryCode) || 'CL';
				const isLogin = dataIdioma?.[2] === 'login';

				if (!dataIdioma || isLogin) {
					localStorage.setItem('lng', `${lang}-${country}`);
					dispatch(
						setLangCountryUser({
							lang,
							country
						})
					);
					i18n.changeLanguage(`${lang}-${country}`);
				} else {
					dispatch(
						setLangCountryUser({
							lang: dataIdioma[0] as LanguageCode,
							country: dataIdioma[1] as CountryCode
						})
					);
					i18n.changeLanguage(`${dataIdioma[0]}-${dataIdioma[1]}`);
				}

				console.log(
					`[UserAuth] monitor(${userContent.hasuraKey}) ${userContent.nombre} loaded for country ${userContent.pais}`
				);
				// dispatch(updateMonitor(monitor));
				listaColegios = monitor.monitor_colegios.map((mc) => mc.colegio);
				avatarPath = user.avatarPath;
				// dispatch(authenticated({ ...userContent, permissions, rol: profesor.rol }));
				dispatch(authenticated({ ...userContent, permissions, listaColegios, avatarPath, cambioPass }));
				dispatch(updateLoginStatus('login'));
				finishLoad();
				return;
			}

			dispatch(logout());
			dispatch(updateLoginStatus('logout'));
			finishLoad();
			// notificationService({ info: true }, 'Usuario no habilitado');
		});
	});
};

export const signInAdmin = () => () => {
	console.log('Login admin');
	const provider = new firebase.auth.GoogleAuthProvider();
	provider.setCustomParameters({
		prompt: 'select_account'
	});

	firebase
		.auth()
		.signInWithPopup(provider)
		.then((result: any) => result.user.getIdToken())
		.then((idToken) => {
			axios.post(`${API_KEY}/auth/setAdminCustomClaims`, {
				idToken,
				tipoUsuario: 'admin'
			});
			window.location.replace('/');
		});
};

export const signInGoogleEstudiante = () => (dispatch: any) => {
	console.log('Login gmail estudiante');
	const provider = new firebase.auth.GoogleAuthProvider();
	provider.setCustomParameters({ prompt: 'select_account' });

	firebase
		.auth()
		.signInWithPopup(provider)
		.then(({ user }) => {
			return user && user.getIdToken(); // TODO: What happens if user is null? is token null?
		})
		.then((idToken) => {
			axios
				.post(`${API_KEY}/auth/estudianteProviderAuth`, {
					idToken,
					tipoUsuario: 'estudiante',
					authType: 'google'
				})
				.then((res) => {
					if (!res?.data || !res?.data?.authorized) {
						notificationService({ info: true }, 'Usuario no autenticado');
						return;
					}
					dispatch(authenticated(res.data));
					dispatch(updateLoginStatus('login'));
					amplitude.getInstance().logEvent('[ESTUDIANTE] Login Google', {
						user_key: res.data.hasuraKey,
						nombre: res.data.nombre
					});
					window.location.replace('/');
				});
		});
};

export const signInMicrosoftEstudiante = () => (dispatch: any) => {
	console.log('Login microsoft estudiante');
	const provider = new firebase.auth.OAuthProvider('microsoft.com');
	firebase
		.auth()
		.signInWithPopup(provider)
		.then(async ({ additionalUserInfo }) => {
			const idToken = await firebase.auth().currentUser?.getIdToken(true);
			let email = '';
			if (additionalUserInfo && additionalUserInfo.profile) {
				const { mail, userPrincipalName }: any = additionalUserInfo.profile;
				email = mail || userPrincipalName || '';
			}
			return { email, idToken };
		})
		.then(({ email, idToken }) => {
			axios
				.post(`${API_KEY}/auth/estudianteProviderAuth`, {
					mail: email,
					idToken,
					authType: 'microsoft',
					tipoUsuario: 'estudiante'
				})
				.then((res) => {
					if (!res?.data || !res?.data?.authorized) {
						notificationService({ info: true }, 'Usuario no autenticado');
						return;
					}
					dispatch(authenticated(res.data));
					dispatch(updateLoginStatus('login'));
					amplitude.getInstance().logEvent('[ESTUDIANTE] Login Microsoft', {
						user_key: res.data.hasuraKey,
						nombre: res.data.nombre
					});
					window.location.replace('/');
				});
		});
};

export const signIngoogleUserCuenta = (setVisible: any, tipoUsuario: TipoUsuarioEnum) => (dispatch: any) => {
	console.log('Login gmail', tipoUsuario);
	const provider = new firebase.auth.GoogleAuthProvider();
	provider.setCustomParameters({ prompt: 'select_account' });

	firebase
		.auth()
		.signInWithPopup(provider)
		.then(({ user }) => {
			return user && user.getIdToken(); // TODO: What happens if user is null? is token null?
		})
		.then((idToken) => {
			axios
				.post(
					`${API_KEY}/auth/userProviderAuth`,
					{
						idToken,
						tipoUsuario,
						setVerifiedEmailCookie: true // Add this line
					},
					{
						withCredentials: true // Add this line
					}
				)
				.then((res: { data: IUserClaimsAuth }) => {
					if (!res?.data || !res?.data?.authorized || !res.data.a) {
						notificationService({ info: true }, 'Usuario no autenticado');
						return;
					}
					let letterTypeUserAuth: 'p' | 'c' | 'm';
					switch (tipoUsuario) {
						case TipoUsuarioEnum.coordinador:
							letterTypeUserAuth = 'c';
							break;
						case TipoUsuarioEnum.monitor:
							letterTypeUserAuth = 'm';
							break;
						default:
							letterTypeUserAuth = 'p';
							break;
					}
					const userList = res.data.l?.[letterTypeUserAuth] || [];
					console.log('signIngoogleUserCuenta', tipoUsuario);
					if (userList.length === 1) {
						console.log('logeas');
						localStorage.setItem('key', userList[0][0]);
						localStorage.setItem('tp', tipoUsuario);
						dispatch(updateLoginStatus('login'));
						amplitude.getInstance().logEvent(`[${tipoUsuario}] Login Google`, {
							user_key: res.data.l.u
						});
						window.location.replace('/');
					}
					if (userList.length === 0) {
						console.log('no logeas');
						notificationService({ info: true }, 'Usuario no habilitado');
						dispatch(logoutAction());
						dispatch(updateLoginStatus('logout'));
						finishLoad();
					} else {
						console.log('mas de un usuario');
						dispatch(authenticated(res.data));
						setVisible(true);
					}
				});
		});
};

export const signInMicrosoftUserCuenta = (setVisible: any, tipoUsuario: TipoUsuarioEnum) => (dispatch: any) => {
	console.log('Login microsoft profesor');
	const provider = new firebase.auth.OAuthProvider('microsoft.com');
	firebase
		.auth()
		.signInWithPopup(provider)
		.then(async ({ additionalUserInfo }) => {
			const idToken = await firebase.auth().currentUser?.getIdToken(true);
			let email = '';
			if (additionalUserInfo && additionalUserInfo.profile) {
				const { mail, userPrincipalName }: any = additionalUserInfo.profile;
				email = mail || userPrincipalName || '';
			}
			return { email, idToken };
		})
		.then(({ email, idToken }) => {
			axios
				.post(
					`${API_KEY}/auth/userProviderAuth`,
					{
						mail: email,
						idToken,
						authType: 'microsoft',
						tipoUsuario,
						setVerifiedEmailCookie: true // Add this line
					},
					{
						withCredentials: true // Add this line
					}
				)
				.then((res: { data: IUserClaimsAuth }) => {
					if (!res?.data || !res?.data?.authorized || !res.data.a) {
						notificationService({ info: true }, 'Usuario no autenticado');
						return;
					}
					let letterTypeUserAuth: 'p' | 'c' | 'm';
					switch (tipoUsuario) {
						case TipoUsuarioEnum.coordinador:
							letterTypeUserAuth = 'c';
							break;
						case TipoUsuarioEnum.monitor:
							letterTypeUserAuth = 'm';
							break;
						default:
							letterTypeUserAuth = 'p';
							break;
					}
					const userList = res.data.l?.[letterTypeUserAuth] || [];
					if (userList.length === 1) {
						localStorage.setItem('key', userList[0][0]);
						localStorage.setItem('tp', tipoUsuario);
						dispatch(updateLoginStatus('login'));
						amplitude.getInstance().logEvent(`[${tipoUsuario}] Login Microsoft`, {
							user_key: res.data.l.u
						});
						window.location.replace('/');
					}
					if (userList.length === 0) {
						notificationService({ info: true }, 'Usuario no habilitado');
						dispatch(logoutAction());
						dispatch(updateLoginStatus('logout'));
						finishLoad();
					} else {
						dispatch(authenticated(res.data));
						setVisible(true);
					}
					// if (!res?.data || !res?.data?.authorized) {
					// 	notificationService({ info: true }, 'Usuario no autenticado');
					// 	return;
					// }
					// dispatch(authenticated(res.data));
					// dispatch(updateLoginStatus('login'));
					// amplitude.getInstance().logEvent('[PROFESOR] Login Microsoft', {
					// 	user_key: res.data.hasuraKey,
					// 	nombre: res.data.nombre
					// });
					// window.location.replace('/');
				});
		});
};

export const verifyEmailWithProvider = (email: string, provider: 'google' | 'microsoft') => {
	return async () => {
		try {
			console.log(`Verifying email ${email} with ${provider}`);

			// Create a secondary Firebase app instance for verification only
			const verificationApp = firebase.initializeApp(
				firebase.app().options,
				`email-verification-${Math.random().toString(36).substring(2, 10)}`
			);

			let authProvider;
			const authType = provider;

			if (provider === 'google') {
				authProvider = new firebase.auth.GoogleAuthProvider();
				authProvider.setCustomParameters({
					prompt: 'select_account',
					login_hint: email // Suggest the email to authenticate
				});
			} else {
				authProvider = new firebase.auth.OAuthProvider('microsoft.com');
				authProvider.setCustomParameters({
					prompt: 'select_account',
					login_hint: email // Suggest the email to authenticate
				});
			}

			// First step: Authenticate with the provider using the secondary app
			let result;
			let idToken;
			let authenticatedEmail;

			if (provider === 'google') {
				result = await verificationApp.auth().signInWithPopup(authProvider);
				authenticatedEmail = result.user?.email;
				idToken = await result.user?.getIdToken(true);
			} else {
				result = await verificationApp.auth().signInWithPopup(authProvider);
				if (result.additionalUserInfo && result.additionalUserInfo.profile) {
					const profile = result.additionalUserInfo.profile as any;
					authenticatedEmail = profile.mail || profile.userPrincipalName || result.user?.email;
				} else {
					authenticatedEmail = result.user?.email;
				}
				idToken = await result.user?.getIdToken(true);
			}

			// Check if the authenticated email matches the requested email
			if (authenticatedEmail?.toLowerCase() !== email.toLowerCase()) {
				console.log('Email mismatch:', authenticatedEmail, 'vs', email);

				// Delete the secondary app
				await verificationApp.delete();

				return {
					success: false,
					message: 'El correo autenticado no coincide con el seleccionado'
				};
			}

			// Second step: Verify with backend
			try {
				const apiParams = {
					idToken,
					...(provider === 'microsoft' && { mail: authenticatedEmail }),
					authType,
					tipoUsuario: 'verificacion',
					verificationOnly: true, // Flag to indicate this is just for verification
					setVerifiedEmailCookie: true // Tell backend to set a secure cookie
				};

				const response = await axios.post(`${API_KEY}/auth/userProviderAuth`, apiParams, {
					withCredentials: true // Important: This allows cookies to be set by the server
				});

				console.log('Email verification response:', response.data);

				// Delete the secondary app
				await verificationApp.delete();

				if (response?.data?.a) {
					return {
						success: true,
						email: authenticatedEmail,
						verified: true,
						accounts: response.data.l
					};
				}
				return {
					success: false,
					message: response?.data?.message || 'No se encontraron cuentas asociadas a este correo'
				};
			} catch (apiError) {
				console.error('Backend verification failed:', apiError);

				// Delete the secondary app
				await verificationApp.delete();

				// Fallback to basic email verification
				// We know the user authenticated successfully with this email
				return {
					success: true,
					email: authenticatedEmail,
					verified: true,
					fallback: true
				};
			}
		} catch (error: any) {
			console.error('Email verification failed:', error);

			// Handle specific Firebase auth errors
			if (error.code === 'auth/popup-closed-by-user') {
				return {
					success: false,
					message: 'Verificación cancelada. Por favor, intente nuevamente.'
				};
			}

			return {
				success: false,
				message: 'La autenticación falló. Por favor, inténtalo de nuevo.'
			};
		}
	};
};

export const fetchVerifiedEmails = async () => {
	try {
		const response = await axios.get(`${API_KEY}/auth/getVerifiedEmails`, {
			withCredentials: true
		});
		// console.log('response', response);
		return response.data.verifiedEmails || {};
	} catch (error) {
		console.error('Failed to fetch verified emails:', error);
		return {};
	}
};
